import React, { memo } from "react";
import {
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  type SelectChangeEvent,
} from "@mui/material";
import { useTranslation } from "react-i18next";

interface Country {
  id: number;
  iso: string;
  name: string;
}

const defaultCountries: Country[] = [
  {
    id: 1,
    iso: "DE",
    name: "Deutschland",
  },
  {
    id: 2,
    iso: "AT",
    name: "Österreich",
  },
  {
    id: 3,
    iso: "ES",
    name: "Spanien",
  },
  {
    id: 4,
    iso: "CH",
    name: "Schweiz",
  },
  {
    id: 5,
    iso: "LU",
    name: "Luxemburg",
  },
  {
    id: 6,
    iso: "IT",
    name: "Italien",
  },
  {
    id: 7,
    iso: "NA",
    name: "Namibia",
  },
  {
    id: 0,
    iso: "ZZ",
    name: "Andere",
  },
];

/**
 * Resolve an ISO-3166-1 alpha-2 code to its localized country name.
 * Falls back to the ISO code itself when unknown so the UI never renders empty.
 */
export function getCountryName(iso: string | null | undefined): string {
  if (!iso) return "";
  const hit = defaultCountries.find((c) => c.iso === iso.toUpperCase());
  return hit?.name ?? iso;
}

const isoAlpha2 = /^[A-Za-z]{2}$/;

/**
 * Maps persisted country values to ISO-3166-1 alpha-2 (APIs, e-invoice BT-55, `selector="iso"`).
 * Accepts codes as-is; maps legacy menu values that stored German display names.
 */
export function normalizeCountryStorageValueToIso(
  value: string | null | undefined,
): string {
  if (!value) return "";
  const v = value.trim();
  if (isoAlpha2.test(v)) return v.toUpperCase();
  const hit = defaultCountries.find(
    (c) => c.name.toLowerCase() === v.toLowerCase(),
  );
  return hit?.iso ?? v;
}

interface CountrySelectProps {
  countries?: Country[];
  selector?: "name" | "iso";
  disabled?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  label?: string;
  fullWidth?: boolean;
}

const CountrySelect: React.FC<CountrySelectProps> = ({
  countries = defaultCountries,
  /** Prefer `iso` so stored values match ISO-3166-1 alpha-2 (billing / compliance). */
  selector = "iso",
  disabled = false,
  value,
  onChange,
  required = false,
  error = false,
  helperText,
  label,
  fullWidth = true,
  ...props
}) => {
  const { t } = useTranslation();

  const handleOnChange = (event: SelectChangeEvent<string>) => {
    if (onChange) {
      onChange(event.target.value);
    }
  };

  return (
    <FormControl
      fullWidth={fullWidth}
      variant="outlined"
      required={required}
      error={error || (required && !value)}
    >
      <InputLabel id="country-select-label">{label || t("Country")}</InputLabel>
      <Select
        labelId="country-select-label"
        id="country-select"
        value={value ?? ""}
        onChange={handleOnChange}
        label={label || t("Country")}
        disabled={disabled}
        {...props}
      >
        {countries.length > 0
          ? countries.map((item) => (
              <MenuItem key={item.id} value={item[selector]}>
                {t(item.name)}
              </MenuItem>
            ))
          : null}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default memo(CountrySelect);
